import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { App as CapacitorApp } from '@capacitor/app';

import { getQueue } from '../offline/offlineQueue';
import { belongsToCurrentSession, currentQueueOwner } from '../offline/queueOwner';
import { flushUnsyncedGpsPoints } from '../utils/gpsFlush';
import { showErrorToast, showSuccessToast } from '../utils/toasts';
import {
  drainNativeGpsBuffer,
  ensureNativeTrackingAlive,
  getPositionSource,
  stopNativeTrackingIfRunning,
  isNativeApp,
  isNativeStartInFlight,
} from '../utils/positionSource';
import useSurveillanceRoutes from './useSurveillanceRoutes';
import { useAuth } from './useAuth';
import { Role } from '../types/auth';
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import {
  gpsPermissionIsDenied,
  systemLocationIsOff,
  trackingStartWouldAsk,
} from '../utils/gpsPermission';
import { isSystemPromptOpen, onSystemPromptClosed } from '../utils/systemPrompts';

/** Un solo aviso de «captura detenida» a la vez, aunque fallen dos ciclos juntos. */
export const GPS_STOPPED_TOAST_ID = 'gps-capture-stopped';

/**
 * Guardián de la captura GPS — vive en el `TimerOverlay`, o sea en TODA la app.
 *
 * ── El fallo que existe para impedir ───────────────────────────────────────
 *
 * Todo esto vivía dentro de `useSurveillanceRouteGpsTracking`, que solo se
 * monta en la pestaña del mapa del recorrido. En cuanto el operario salía de
 * ahí —a la pestaña de al lado, a crear la actuación básica del tramo, a
 * mirar otra OT— se iban con la pantalla el vigilante que resucita el servicio
 * nativo, el drenaje de lo capturado y el envío al servidor.
 *
 * Consecuencia medida en una tablet real (12-ago-2026): la captura murió a las
 * 12:42 y **no volvió en 47 horas**, con la app en uso y el coche rodando.
 * Nadie la revivió porque el único que sabía hacerlo estaba en una pantalla
 * que el operario ya no tenía abierta. Y durante todo ese tiempo la sala vio
 * la patrulla congelada, porque tampoco había quien mandara puntos.
 *
 * Es el mismo defecto que ya se corrigió con el Wake Lock y con el aviso de
 * actualización: **atar algo al ciclo de vida de un componente cuando en
 * realidad depende de una condición de negocio** ("¿hay un recorrido en curso
 * que este dispositivo esté haciendo?").
 *
 * ── Qué hace ───────────────────────────────────────────────────────────────
 *
 * Mientras hay recorrido que capturar: revive el servicio si el sistema lo ha
 * matado, drena lo que el servicio guardó en SQLite y lo manda al backend.
 * Cuando no lo hay: apaga el servicio para que no quede huérfano gastando
 * batería y GPS.
 *
 * Lo que NO hace: escuchar posiciones. Eso sigue en la pantalla del mapa,
 * porque solo ella dibuja el cursor y la traza en vivo.
 */

/**
 * Cada cuánto corre el ciclo del guardián: comprobar el permiso, comprobar que
 * el servicio sigue vivo, drenar el buffer nativo y mandar lo pendiente.
 *
 * Antes había además una constante `WATCHDOG_INTERVAL_MS = 60_000` para el
 * vigilante. Quedó sin usar el día que se le subió la cadencia a la de entrega,
 * y esa mezcla es la que dejó dos temporizadores entregando a la vez.
 */
const DELIVERY_INTERVAL_MS = 15_000;

/**
 * Operaciones encoladas que significan "hay un recorrido en marcha aunque el
 * servidor todavía no lo sepa". Con una de estas esperando NO se apaga nada:
 * un tramo iniciado sin cobertura no aparece en `activeSurveillanceRoutes`, y
 * apagar la captura por eso sería exactamente el fallo que este hook viene a
 * arreglar, pero peor.
 */
/**
 * Freno entre ciclos disparados por `resume`. Android encadena eventos de
 * reanudación cuando se le piden permisos ya denegados, y sin freno cada uno
 * lanzaría su ciclo.
 */
const MIN_MS_BETWEEN_CYCLES = 5_000;

const OPS_THAT_IMPLY_CAPTURE = new Set([
  'StartSurveillanceRoute',
  'CreateRouteSegment',
  'FinishRouteSegment',
  'AddGpsPointsBatch',
]);

interface UseGpsCaptureGuardianOptions {
  /**
   * Recorrido que ESTE dispositivo debe estar capturando, o `undefined` si no
   * hay ninguno. Lo calcula el `TimerOverlay` a partir de los recorridos
   * activos y de quién es el usuario — nunca de la pantalla que esté abierta.
   */
  routeId?: string;
  /**
   * `true` solo cuando de verdad sabemos qué recorridos hay activos. Sin esto,
   * el primer render (todavía sin datos) parecería "no hay nada que capturar"
   * y apagaría el servicio de una patrulla en ruta.
   */
  routesKnown: boolean;
}

export function useGpsCaptureGuardian({
  routeId,
  routesKnown,
}: UseGpsCaptureGuardianOptions): void {
  const { t } = useTranslation(['toasts']);
  const { addGpsPointsBatch } = useSurveillanceRoutes();

  // Latest-ref: `useSurveillanceRoutes` recrea sus funciones en cada render y
  // con la función en las dependencias los intervalos se derribarían y
  // reconstruirían constantemente (mismo motivo que en el hook de la pantalla).
  const sendRef = useRef(addGpsPointsBatch);
  sendRef.current = addGpsPointsBatch;

  const tRef = useRef(t);
  tRef.current = t;

  // ── Vigilancia del permiso de ubicación ─────────────────────────────────
  //
  // El aviso de «Es necesario reactivar el GPS» solo salía desde la pantalla
  // del mapa, y un recorrido puede estar grabando sin que nadie la mire: el
  // cronómetro de la cabecera abre y cierra tramos desde cualquier pantalla.
  // Si en ese rato se revoca la ubicación, el operario no se entera — y el
  // silencio es indistinguible de que todo va bien.
  //
  // La comprobación vive aquí porque aquí es donde la pregunta tiene
  // respuesta: este hook ya sabe si hay un recorrido que ESTE dispositivo
  // deba estar capturando.
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isTablet = (user?.user?.rolActive as Role | undefined) === Role.Tablet;

  // Qué hay abierto en la ÚNICA ranura de modal de la app. Se lee para no
  // pisar lo que el operario esté haciendo: matar su ventana sería peor que
  // callarse (ya pasó con la ráfaga de la cámara y el visor del RV).
  const currentModalId = useSelector(
    (estado: { modal?: { id?: string | null } }) => estado.modal?.id ?? null
  );
  const currentModalIdRef = useRef<string | null>(currentModalId);
  currentModalIdRef.current = currentModalId;

  /** ¿El bloqueante que hay abierto lo puso este hook? Solo eso se cierra. */
  const ownBlockerRef = useRef(false);

  /**
   * Lo que hace el ciclo en cuanto «Activar GPS» termina (lo pone el efecto
   * de abajo, que es quien tiene el ciclo).
   */
  const recheckRef = useRef<() => void>(() => {});

  // 🔴 Al terminar, un ciclo AL MOMENTO: es el ciclo quien cierra este aviso y
  // arranca el GPS. Visto en la W el 25-09-2026: tras «Cambiar a ubicación
  // precisa» el aviso seguía en pantalla hasta el ciclo siguiente (hasta 15 s;
  // el de `resume` se descarta si hubo otro en los últimos 5 s) y el operario
  // volvía a pulsar «Activar GPS». La pantalla del recorrido ya cierra el suyo
  // en el acto desde julio (`useSurveillanceRouteGpsTracking`).
  const requestGps = useCallback(
    (): Promise<'granted' | 'denied' | 'no-fix'> =>
      new Promise<'granted' | 'denied' | 'no-fix'>((resolve) => {
        const source = getPositionSource();
        if (!source) {
          resolve('no-fix');
          return;
        }
        source.getCurrent(
          () => resolve('granted'),
          (err) => resolve(err.code === 1 ? 'denied' : 'no-fix')
        );
      }).then((result) => {
        recheckRef.current();
        return result;
      }),
    []
  );

  /**
   * ¿Estaba vivo el servicio la última vez que miramos? Sirve para avisar solo
   * en el FLANCO (vivo → muerto), no en cada vuelta del reloj.
   */
  const wasAliveRef = useRef(true);

  useEffect(() => {
    if (!routeId) return;

    // Al entrar en un recorrido nuevo se parte de "vivo": el primer ciclo del
    // watchdog es quien dice la verdad, y así no se avisa por arrancar.
    wasAliveRef.current = true;

    const deliver = async (): Promise<void> => {
      try {
        if (isNativeApp()) await drainNativeGpsBuffer(routeId);
      } catch (err) {
        // `drain` no borra nada, así que el siguiente ciclo relee lo mismo.
        console.warn('[SR-GPS] no se pudo drenar el buffer nativo', err);
      }
      try {
        await flushUnsyncedGpsPoints(routeId, (id, points) =>
          sendRef.current(id, points)
        );
      } catch (err) {
        // Los puntos siguen sin marcar en IndexedDB: los reintenta el próximo
        // ciclo. Sin toast: es una operación de fondo y de alta frecuencia.
        console.warn('[SR-GPS] envío fallido, se reintenta', err);
      }
    };

    const watchService = async (permissionDenied: boolean): Promise<void> => {
      if (!isNativeApp()) return;
      // 🔴 Sin permiso NO se intenta arrancar el servicio, y no es solo que sea
      // inútil: `start()` PIDE los permisos que falten, y esa petición pasa por
      // el ciclo pausa/reanudación de Android aunque el sistema conteste al
      // instante («denegado para siempre»). El listener de `resume` de este
      // mismo hook vuelve a llamar aquí, que vuelve a llamar a `start()`… y la
      // app entra en un bucle cerrado de llamadas al puente que la tumba.
      // Medido en tablet el 2026-09-01 con el permiso denegado.
      if (permissionDenied) return;
      // Ni con la hoja de Google «¿Guardar contraseña?» encima SI `start()`
      // tuviera que pedir algún permiso: su diálogo saldría encima de la hoja
      // y la respuesta no llegaría hasta cerrarla (medido en la W el 25/09).
      // Al cerrarse se lanza un ciclo (abajo). Con todo concedido no pide
      // nada, así que se arranca sin esperar: no se deja de grabar por ella.
      if (isSystemPromptOpen() && (await trackingStartWouldAsk())) return;
      // Ni con otro `start()` esperando a que el operario conteste su diálogo:
      // uno nuevo haría que Android diera aquel por denegado (ver
      // `isNativeStartInFlight`). Cuando conteste, el siguiente ciclo verá el
      // servicio en marcha, o el permiso rechazado.
      if (isNativeStartInFlight()) return;
      try {
        const revived = await ensureNativeTrackingAlive(routeId);
        if (!revived) {
          wasAliveRef.current = true;
          return;
        }
        // Cuántas veces hay que revivirlo ES la métrica de salud del
        // dispositivo: distingue "esta tablet está bien configurada" de "este
        // modelo no sirve para esto".
        Sentry.captureMessage(
          '[SR-GPS] foreground service revived por el guardián',
          {
            level: 'warning',
            tags: { feature: 'surveillance-route', hook: 'useGpsCaptureGuardian' },
          }
        );
        // 🔴 El operario tiene que enterarse. Hasta ahora, cuando la captura
        // se caía, el único indicio era una notificación que desaparecía de la
        // cortina — y nadie mira la cortina mientras conduce.
        if (wasAliveRef.current) {
          showSuccessToast(tRef.current('toasts:gpsCapture.resumed'));
        }
        wasAliveRef.current = true;
      } catch (err) {
        // No se ha podido resucitar: esto sí es pérdida de traza en curso y el
        // operario debe saberlo para poder reaccionar (reabrir el recorrido,
        // revisar permisos, avisar a sala).
        Sentry.captureException(err, {
          tags: { feature: 'surveillance-route', hook: 'useGpsCaptureGuardian' },
        });
        // 🔴 Con id fijo: al entrar, el efecto se monta dos veces seguidas y
        // dos ciclos fallan a la vez — salían DOS avisos rojos idénticos
        // montados uno sobre otro (visto en la W el 25-09-2026).
        //
        // Y no si lo que falla es el PERMISO: para eso está el aviso
        // bloqueante del permiso, que dice qué tocar. Este mandaba a «volver a
        // abrir el recorrido», que no lo arregla (visto en la W el 25-09-2026
        // con la ubicación aproximada).
        const permissionDenied =
          (err as { code?: string } | undefined)?.code === 'PERMISSION_DENIED';
        if (wasAliveRef.current && !permissionDenied) {
          showErrorToast(tRef.current('toasts:gpsCapture.stopped'), {
            toastId: GPS_STOPPED_TOAST_ID,
          });
        }
        wasAliveRef.current = false;
        // Un `start()` rechazado por permiso ES la negativa: el aviso, ya. Si
        // no, esperaba al ciclo siguiente (~5 s en la W el 25-09-2026, tras
        // «aproximada» y «No permitir» a las notificaciones).
        if (permissionDenied) await watchPermission(true);
      }
    };

    /**
     * Cuánto hay que esperar como mínimo entre dos ciclos. Sin este freno, un
     * `resume` que se repita —y con los permisos denegados Android los encadena—
     * dispararía un ciclo por evento.
     */
    let lastCycleAt = 0;

    /**
     * ¿Sigue habiendo permiso para grabar? Solo mira la DENEGACIÓN, que es una
     * decisión del usuario o del sistema y no se arregla sola. La falta de
     * señal —un túnel, el aparcamiento de la base— es transitoria y bloquear
     * por ella sería la cárcel sin salida que ya se corrigió en julio.
     */
    const watchPermission = async (denied: boolean): Promise<void> => {
      // FR-14.1: el bloqueante es solo para Tablet. Los demás roles pueden
      // estar mirando el mapa en modo lectura y exigirles GPS no tiene sentido.
      if (!isTablet) return;

      if (denied) {
        if (ownBlockerRef.current) return;
        // Hay otra ventana abierta (o el bloqueante ya lo puso la pantalla del
        // mapa): no se pisa. Se reintenta en el ciclo siguiente.
        if (currentModalIdRef.current) return;
        ownBlockerRef.current = true;
        dispatch(
          openModal(modalIds.GPS_REQUIRED, { onRequestGps: requestGps })
        );
        return;
      }

      // El permiso ha vuelto: se retira SOLO si el bloqueante es el nuestro y
      // sigue siendo el que está en pantalla.
      if (!ownBlockerRef.current) return;
      ownBlockerRef.current = false;
      if (currentModalIdRef.current === modalIds.GPS_REQUIRED) {
        dispatch(closeModal());
      }
    };

    /**
     * Un ciclo completo, con UNA sola consulta del permiso para las dos cosas
     * que dependen de él: avisar al operario y decidir si se puede arrancar.
     */
    const runCycle = async (forced = false): Promise<void> => {
      const ahora = Date.now();
      if (!forced && ahora - lastCycleAt < MIN_MS_BETWEEN_CYCLES) return;
      lastCycleAt = ahora;

      let denied = false;
      if (isTablet) {
        try {
          // Dos causas distintas con el mismo remedio para el operario: el
          // permiso revocado y la ubicación del aparato apagada. La segunda no
          // la ve nadie más — en la app la posición la emite el servicio, no
          // `navigator.geolocation`, así que con la ubicación apagada no hay
          // error ninguno: el servicio sigue diciendo que corre y no llega un
          // solo punto.
          //
          // Y NO vale mirar si llegan puntos: el servicio solo emite al moverse
          // 5 m (`setMinUpdateDistanceMeters`), o sea que el silencio es
          // también lo normal en una actuación de veinte minutos parado.
          denied =
            (await gpsPermissionIsDenied()) || (await systemLocationIsOff());
        } catch {
          // Ante la duda, seguir como si hubiera permiso: un falso bloqueante
          // tapa la pantalla entera y es peor que no avisar.
          denied = false;
        }
      }
      await watchPermission(denied);
      await watchService(denied);
      // El envío SÍ se hace aunque no haya permiso: puede quedar traza vieja
      // sin mandar, y perderla sería el fallo que todo esto quiere evitar.
      await deliver();
    };

    // Tras «Activar GPS»: un ciclo ya, y otro al volver a la app si el
    // diálogo de Android aún estaba encima (entonces el ciclo no arranca el
    // GPS: ver `watchService`). Forzados: el freno de 5 s es para las rachas
    // de `resume`, no para la respuesta del operario.
    let recheckOnResume = false;
    recheckRef.current = () => {
      recheckOnResume = true;
      void runCycle(true);
    };

    // Un ciclo inmediato: si se llega aquí volviendo de otra pantalla o de
    // reabrir la app, lo importante es no esperar un minuto para comprobarlo.
    void runCycle(true);
    const offPromptClosed = onSystemPromptClosed(() => void runCycle(true));

    // 🔴 UN SOLO temporizador, y va a la cadencia de ENTREGA (15 s), no a la
    // del vigilante del servicio (60 s): un minuto conduciendo sin grabar y sin
    // saberlo es demasiado, y comprobar el permiso es una consulta local que no
    // toca el GPS.
    //
    // Antes había DOS a esta misma cadencia y los dos acababan entregando —uno
    // llamando a `deliver()` a pelo y este, porque `runCycle` termina siempre en
    // `await deliver()`—, así que el buffer nativo se drenaba y se confirmaba
    // dos veces cada 15 s. Medido en tablet: 85 `drain` por 42 `isRunning`.
    // Vigilar y entregar van juntos en el mismo ciclo; separarlos no aportaba
    // nada y costaba el doble de cruces del puente nativo.
    //
    // ⚠️ Lo ÚNICO que se pierde con la unificación: si el puente nativo se
    // quedara COLGADO (sin resolver ni rechazar) en `ensureNativeTrackingAlive`,
    // el ciclo no llegaría a entregar, y antes el temporizador suelto sí habría
    // seguido mandando al backend lo que ya estuviera en IndexedDB. Se asume a
    // propósito: con el puente colgado, `drain` —que la entrega también
    // necesita— se colgaría igual, la captura ya estaría muerta y el vigilante
    // tampoco podría detectarlo. Y lo capturado no se pierde: sigue en
    // IndexedDB y sale en el ciclo siguiente, al reanudar la app o al
    // desmontar. Si algún día se ve un cuelgue real, el remedio es un tope de
    // tiempo en la llamada al puente, no volver a dos temporizadores.
    const watchdogTimer = setInterval(
      () => void runCycle(true),
      DELIVERY_INTERVAL_MS
    );

    // `resume` de Capacitor: el momento en que más urge comprobarlo, porque es
    // cuando el operario vuelve a mirar y espera ver la traza al día.
    let cancelled = false;
    let resumeHandle: { remove: () => Promise<void> } | null = null;
    if (isNativeApp()) {
      void CapacitorApp.addListener('resume', () => {
        if (recheckOnResume) {
          recheckOnResume = false;
          void runCycle(true);
          return;
        }
        // Con freno: ver `MIN_ENTRE_CICLOS_MS`.
        void runCycle();
      }).then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        resumeHandle = handle;
      });
    }

    return () => {
      cancelled = true;
      recheckRef.current = () => {};
      clearInterval(watchdogTimer);
      offPromptClosed();
      if (resumeHandle) void resumeHandle.remove();
      // 🔴 Si el bloqueante lo pusimos nosotros, hay que retirarlo AQUÍ. Es un
      // modal que no se puede cerrar —ni con la X, ni tocando fuera, ni con
      // Escape— y su único dueño es este efecto: cuando el recorrido deja de
      // estar activo (lo cierra otro dispositivo, la consulta de activos deja
      // de verlo, se cierra sesión) el reloj se para y nadie volvería a
      // mirarlo. El operario se quedaría encerrado detrás de una ventana que
      // ya no significa nada.
      if (ownBlockerRef.current) {
        ownBlockerRef.current = false;
        if (currentModalIdRef.current === modalIds.GPS_REQUIRED) {
          dispatch(closeModal());
        }
      }
      // Último envío best-effort: acorta la latencia de entrega del último
      // tramo de puntos. Si falla no se pierde nada — siguen en IndexedDB.
      void deliver();
    };
  }, [routeId, dispatch, isTablet, requestGps]);

  /**
   * Servicio huérfano: sin recorrido que capturar no debe haber servicio, ni
   * notificación permanente, ni gasto de batería.
   *
   * Las tres condiciones para apagar son deliberadamente estrictas, porque
   * equivocarse aquí es cortarle la traza a una patrulla:
   *  1. sabemos qué recorridos hay activos (no es el render inicial vacío),
   *  2. hay red — sin ella la lista de activos no significa nada,
   *  3. la cola no tiene ninguna operación de ESTA sesión que implique un
   *     recorrido en marcha (un tramo iniciado sin cobertura todavía no existe
   *     para el servidor). Lo de otro usuario no cuenta: cerrar sesión ya no
   *     vacía la cola (spec 2026-10-12) y esas operaciones no salen hasta que
   *     su dueño vuelva; con ellas el servicio seguiría encendido —y apuntando
   *     al recorrido del otro— durante toda la sesión del siguiente.
   *
   * Ante la duda se deja encendido: un servicio de más gasta batería, uno de
   * menos pierde el trabajo de un turno y no hay forma de recuperarlo.
   */
  useEffect(() => {
    if (routeId || !routesKnown || !isNativeApp()) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    let cancelled = false;
    void (async () => {
      try {
        const queue = await getQueue();
        if (cancelled) return;
        // Sin sesión legible no se sabe de quién es cada una: cuenta todo
        // (ante la duda, encendido). `belongsToCurrentSession` responde lo
        // contrario en ese caso, que es lo correcto para ENVIAR, no aquí.
        const owner = currentQueueOwner();
        const hayCapturaPendiente = queue.some(
          (op) =>
            OPS_THAT_IMPLY_CAPTURE.has(op.operationName) &&
            (!owner || belongsToCurrentSession(op.owner, owner))
        );
        if (hayCapturaPendiente) return;
        await stopNativeTrackingIfRunning();
      } catch (err) {
        console.warn('[SR-GPS] no se pudo parar el servicio huérfano', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeId, routesKnown]);
}

export default useGpsCaptureGuardian;
